"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

type Direction = "to-smart" | "to-straight";

/** English strings. A translated content file overrides these through `ui`. */
export const QUOTATION_MARK_CONVERTER_STRINGS = {
  direction: "Direction",
  directionToSmart: "Straight → smart (curly)",
  directionToStraight: "Smart → straight",
  alsoConvert: "Also convert",
  apostrophesOption: "Apostrophes (don't → don’t)",
  emDashOption: "Double hyphen -- to em dash —",
  ellipsisOption: "Three dots ... to ellipsis …",
  inputText: "Input text",
  inputPlaceholder: "She said, \"It's the best result we've seen.\"",
  convert: "Convert",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  convertedOutput: "Converted output",
  conversionsMade: "one:{count} conversion made|other:{count} conversions made",
} as const;

const DIRECTIONS: {
  value: Direction;
  labelKey: "directionToSmart" | "directionToStraight";
}[] = [
  { value: "to-smart", labelKey: "directionToSmart" },
  { value: "to-straight", labelKey: "directionToStraight" },
];

const OPENING_PRECEDER_RE = /[\s([{‘“–—-]/;
const WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const STRAIGHT_DOUBLE_RE = /"/g;
/*
 * Marks that look like opening quotes but are not.
 *
 * A single quote that opens a word because letters or digits were dropped is an
 * apostrophe and faces right: 'tis, 'twas, 'em, 'cause, '90s. A quote around a bare
 * number or word ('90', 'tis') still is a quote, so a closing ' after the word rules it
 * out. Feet and inches (5'10") are primes, not quotes.
 */
const ELIDED_YEAR_RE = /^\d{2}s?(?![\p{L}\p{N}'])/u;
const ELIDED_WORD_RE =
  /^(?:tis|twas|twere|twill|twould|em|cause|til|bout|cos)(?![\p{L}\p{N}'])/iu;
const FEET_INCHES_RE = /(\d+)'(\s?)(\d{1,2}(?:\.\d+)?)"/g;
const STRAIGHT_SINGLE_RE = /'/g;
const DOUBLE_HYPHEN_RE = /--/g;
const THREE_DOTS_RE = /\.{3}/g;
const SMART_DOUBLE_RE = /[“”„]/g;
const SMART_SINGLE_RE = /[‘’‚]/g;
const EM_DASH_RE = /—/g;
const ELLIPSIS_RE = /…/g;

export interface ConvertOptions {
  apostrophes: boolean;
  emDash: boolean;
  ellipsis: boolean;
}

export interface ConversionResult {
  text: string;
  count: number;
}

function isOpeningContext(text: string, offset: number): boolean {
  if (offset === 0) {
    return true;
  }
  return OPENING_PRECEDER_RE.test(text.charAt(offset - 1));
}

function opensElidedWord(text: string, offset: number): boolean {
  const tail = text.slice(offset + 1);
  return ELIDED_YEAR_RE.test(tail) || ELIDED_WORD_RE.test(tail);
}

function countMatches(text: string, re: RegExp): number {
  return text.match(re)?.length ?? 0;
}

function convertStraightSingles(
  text: string,
  apostrophes: boolean
): ConversionResult {
  let count = 0;
  const result = text.replace(
    STRAIGHT_SINGLE_RE,
    (match: string, offset: number, str: string) => {
      const prevIsWord =
        offset > 0 && WORD_CHAR_RE.test(str.charAt(offset - 1));
      if (prevIsWord) {
        if (!apostrophes) {
          return match;
        }
        count += 1;
        return "’";
      }
      if (isOpeningContext(str, offset)) {
        if (opensElidedWord(str, offset)) {
          if (!apostrophes) {
            return match;
          }
          count += 1;
          return "’";
        }
        count += 1;
        return "‘";
      }
      count += 1;
      return "’";
    }
  );
  return { text: result, count };
}

export function toSmart(input: string, opts: ConvertOptions): ConversionResult {
  let count = 2 * countMatches(input, FEET_INCHES_RE);
  const measured = input.replace(FEET_INCHES_RE, "$1′$2$3″");
  count += countMatches(measured, STRAIGHT_DOUBLE_RE);
  let text = measured.replace(
    STRAIGHT_DOUBLE_RE,
    (_match: string, offset: number, str: string) => {
      if (isOpeningContext(str, offset)) {
        return "“";
      }
      return "”";
    }
  );
  const singles = convertStraightSingles(text, opts.apostrophes);
  text = singles.text;
  count += singles.count;
  if (opts.emDash) {
    count += countMatches(text, DOUBLE_HYPHEN_RE);
    text = text.replace(DOUBLE_HYPHEN_RE, "—");
  }
  if (opts.ellipsis) {
    count += countMatches(text, THREE_DOTS_RE);
    text = text.replace(THREE_DOTS_RE, "…");
  }
  return { text, count };
}

export function toStraight(
  input: string,
  opts: ConvertOptions
): ConversionResult {
  let count = countMatches(input, SMART_DOUBLE_RE);
  let text = input.replace(SMART_DOUBLE_RE, '"');
  if (opts.apostrophes) {
    count += countMatches(text, SMART_SINGLE_RE);
    text = text.replace(SMART_SINGLE_RE, "'");
  }
  if (opts.emDash) {
    count += countMatches(text, EM_DASH_RE);
    text = text.replace(EM_DASH_RE, "--");
  }
  if (opts.ellipsis) {
    count += countMatches(text, ELLIPSIS_RE);
    text = text.replace(ELLIPSIS_RE, "...");
  }
  return { text, count };
}

function convert(
  input: string,
  direction: Direction,
  opts: ConvertOptions
): ConversionResult {
  if (direction === "to-smart") {
    return toSmart(input, opts);
  }
  return toStraight(input, opts);
}

export default function QuotationMarkConverterWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(QUOTATION_MARK_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("to-smart");
  const [apostrophes, setApostrophes] = useState(true);
  const [emDash, setEmDash] = useState(false);
  const [ellipsis, setEllipsis] = useState(false);
  const [output, setOutput] = useState("");
  const [conversionCount, setConversionCount] = useState(0);
  const [hasResult, setHasResult] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleConvert() {
    if (input.length === 0) {
      return;
    }
    const result = convert(input, direction, { apostrophes, emDash, ellipsis });
    setOutput(result.text);
    setConversionCount(result.count);
    setHasResult(true);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setConversionCount(0);
    setHasResult(false);
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="convert-direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {strings[d.labelKey]}
            </label>
          ))}
        </div>
      </section>

      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.alsoConvert}
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={apostrophes}
              className="accent-primary"
              onChange={(e) => setApostrophes(e.target.checked)}
              type="checkbox"
            />
            {strings.apostrophesOption}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={emDash}
              className="accent-primary"
              onChange={(e) => setEmDash(e.target.checked)}
              type="checkbox"
            />
            {strings.emDashOption}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={ellipsis}
              className="accent-primary"
              onChange={(e) => setEllipsis(e.target.checked)}
              type="checkbox"
            />
            {strings.ellipsisOption}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="quotes-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="quotes-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleConvert}
          type="button"
        >
          {strings.convert}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {hasResult && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="quotes-output"
          >
            {strings.convertedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="quotes-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.conversionsMade, locale, conversionCount, {
              count: fmt(conversionCount),
            })}
          </p>
        </div>
      )}
    </>
  );
}
